// ============================================
// COMPANY SIDEBAR - Mobil Uyumlu
// ============================================

import { Link, useRouterState } from '@tanstack/react-router';
import {
  LayoutDashboard,
  Briefcase,
  FileText,
  Users,
  Building2,
  Settings,
  HelpCircle,
  LogOut,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Facebook,
  Instagram,
  Linkedin,
  X,
  ArrowLeft,
} from 'lucide-react';
import { cn } from '@/utils';
import { useAuthStore } from '@/store/auth';
import { useTranslation } from '@crew-at-sea/i18n';

// Sosyal medya linkleri
const SOCIAL_LINKS = {
  facebook: 'https://facebook.com/crewatsea.tr',
  instagram: 'https://instagram.com/crewatsea.tr',
  linkedin: 'https://www.linkedin.com/company/crew-at-sea/',
};

interface CompanySidebarProps {
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export function CompanySidebar({ collapsed = false, onCollapsedChange, mobileOpen = false, onMobileClose }: CompanySidebarProps) {
  const { t } = useTranslation('panel');
  const router = useRouterState();
  const currentPath = router.location.pathname;
  const { logout } = useAuthStore();

  const sidebarSections = [
    {
      title: t('sidebar.recruitment'),
      items: [
        { id: 'jobs', label: t('sidebar.jobs'), href: '/jobs', icon: <Briefcase className="h-5 w-5" /> },
        { id: 'applications', label: t('sidebar.applications'), href: '/applications', icon: <FileText className="h-5 w-5" /> },
        { id: 'candidates', label: t('sidebar.candidates'), href: '/candidates', icon: <Users className="h-5 w-5" /> },
      ],
    },
    {
      title: t('sidebar.company'),
      items: [
        { id: 'profile', label: t('sidebar.companyProfile'), href: '/profile', icon: <Building2 className="h-5 w-5" /> },
      ],
    },
    {
      title: t('sidebar.settingsSection'),
      items: [
        { id: 'subscription', label: t('sidebar.subscription'), href: '/subscription', icon: <CreditCard className="h-5 w-5" /> },
        { id: 'settings', label: t('sidebar.settings'), href: '/settings', icon: <Settings className="h-5 w-5" /> },
        { id: 'help', label: t('sidebar.help'), href: '/help', icon: <HelpCircle className="h-5 w-5" /> },
      ],
    },
  ];

  const isActive = (href: string) => {
    if (href === '/') return currentPath === '/';
    return currentPath.startsWith(href);
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  // Mobilde link tıklanınca sidebar'ı kapat
  const handleLinkClick = () => {
    onMobileClose?.();
  };

  return (
    <>
      {/* Mobil Backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm md:hidden"
          onClick={onMobileClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col bg-white border-r border-gray-200 transition-all duration-300',
          // Desktop: collapse durumuna göre genişlik
          collapsed ? 'md:w-16' : 'md:w-64',
          // Mobil: overlay olarak açılır, her zaman geniş
          'w-72',
          mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        )}
      >
        {/* Logo + Mobil Kapat */}
        <div
          className={cn(
            'flex items-center h-16 px-4 border-b border-gray-200',
            collapsed ? 'md:justify-center' : 'gap-3'
          )}
        >
          <div className="flex items-center justify-center w-10 h-10 flex-shrink-0">
            <img src="/favicon.svg" alt="Crew At Sea" className="w-10 h-10" />
          </div>
          {/* Mobilde her zaman göster, desktop'ta collapse değilse göster */}
          <div className={cn('flex-1', collapsed && 'md:hidden')}>
            <h1 className="text-lg font-bold text-gray-900">Crew At Sea</h1>
            <p className="text-xs text-gray-500">{t('sidebar.companyPanel')}</p>
          </div>
          {/* Mobil kapat butonu */}
          <button
            onClick={onMobileClose}
            className="p-2 rounded-maritime text-gray-400 hover:text-gray-600 hover:bg-gray-100 md:hidden"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Ana Sayfaya Dön */}
        <div className="border-b border-gray-200 p-2">
          <a
            href="https://crewatsea.com"
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-maritime transition-colors",
              collapsed && "md:justify-center md:px-2"
            )}
            title={collapsed ? "Ana Sayfaya Dön" : undefined}
          >
            <ArrowLeft className="w-5 h-5 flex-shrink-0 text-gray-400" />
            <span className={cn('flex-1', collapsed && 'md:hidden')}>Ana Sayfaya Dön</span>
          </a>
        </div>

        {/* Navigasyon */}
        <nav className="flex-1 overflow-y-auto py-4 px-2 scrollbar-thin">
          {sidebarSections.map((section, sectionIndex) => (
            <div key={sectionIndex} className={cn(sectionIndex > 0 && 'mt-6')}>
              {/* Section başlığı: mobilde her zaman göster, desktop'ta collapse değilse */}
              {section.title && (
                <h3 className={cn(
                  'mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-gray-400',
                  collapsed && 'md:hidden'
                )}>
                  {section.title}
                </h3>
              )}
              <div className="space-y-1">
                {section.items.map((item) => (
                  <Link
                    key={item.id}
                    to={item.href}
                    onClick={handleLinkClick}
                    className={cn(
                      'flex items-center gap-3 rounded-maritime px-3 py-2.5 text-sm font-medium transition-colors',
                      isActive(item.href)
                        ? 'bg-maritime-50 text-maritime-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                      collapsed && 'md:justify-center md:px-2'
                    )}
                    title={collapsed ? item.label : undefined}
                  >
                    <span
                      className={cn(
                        'flex-shrink-0',
                        isActive(item.href) ? 'text-maritime-600' : 'text-gray-400'
                      )}
                    >
                      {item.icon}
                    </span>
                    {/* Mobilde her zaman göster, desktop'ta collapse değilse */}
                    <span className={cn('flex-1', collapsed && 'md:hidden')}>{item.label}</span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Sosyal Medya */}
        <div className={cn('px-4 py-3 border-t border-gray-200', collapsed && 'md:hidden')}>
          <p className="text-xs text-gray-400 mb-2">{t('sidebar.followUs')}</p>
          <div className="flex gap-2">
            <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-maritime-100 flex items-center justify-center transition-colors" title="Facebook">
              <Facebook className="w-4 h-4 text-gray-600" />
            </a>
            <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-maritime-100 flex items-center justify-center transition-colors" title="Instagram">
              <Instagram className="w-4 h-4 text-gray-600" />
            </a>
            <a href={SOCIAL_LINKS.linkedin} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-maritime-100 flex items-center justify-center transition-colors" title="LinkedIn">
              <Linkedin className="w-4 h-4 text-gray-600" />
            </a>
          </div>
        </div>

        {/* Çıkış Butonu */}
        <div className="border-t border-gray-200 p-2">
          <button
            onClick={handleLogout}
            className={cn(
              'flex items-center gap-3 rounded-maritime px-3 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors w-full',
              collapsed && 'md:justify-center md:px-2'
            )}
            title={collapsed ? t('sidebar.logout') : undefined}
          >
            <LogOut className="h-5 w-5 flex-shrink-0 text-gray-400" />
            <span className={cn(collapsed && 'md:hidden')}>{t('sidebar.logout')}</span>
          </button>
        </div>

        {/* Daralt Butonu - sadece desktop */}
        {onCollapsedChange && (
          <div className="border-t border-gray-200 p-2 hidden md:block">
            <button
              onClick={() => onCollapsedChange(!collapsed)}
              className={cn(
                'flex items-center gap-3 rounded-maritime px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors w-full',
                collapsed && 'justify-center px-2'
              )}
            >
              {collapsed ? (
                <ChevronRight className="h-5 w-5" />
              ) : (
                <>
                  <ChevronLeft className="h-5 w-5" />
                  <span>{t('sidebar.collapse')}</span>
                </>
              )}
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
